import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useWithdrawals } from "../../contexts/WithdrawalContext";
import { usePaymentMethods } from "../../contexts/PaymentMethodsContext";
import { useAppSelector } from "../../redux/store";
import { Button } from "../ui/Button";
import { X, DollarSign, Loader2, CheckCircle, AlertCircle, Shield, CreditCard, Plus, Building2 } from "lucide-react";
import CurrencyDropdown from "../ui/CurrencyDropdown";
import { stripeConnectAPI } from "../../services/stripeConnectAPI";
import { getDynamicCurrencySymbol } from "../../lib/currencyUtils";

const WithdrawalForm: React.FC<{ 
  onClose: () => void; 
  availableBalance?: number;
  onNavigateToWithdrawalMethods?: () => void;
}> = ({ onClose, availableBalance, onNavigateToWithdrawalMethods }) => {
  const { t } = useTranslation();
  const { createWithdrawal } = useWithdrawals();
  const { paymentMethods, refreshPaymentMethods } = usePaymentMethods();
  const user = useAppSelector((state) => state.auth.user);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<{id: string; name: string; code: string} | null>(null);
  const [localStripeStatus, setLocalStripeStatus] = useState<{
    hasAccount: boolean;
    isEnabled: boolean;
    accountId?: string;
  } | null>(null);
  const [checkingStripeStatus, setCheckingStripeStatus] = useState(false);
  const [formData, setFormData] = useState({
    amount: availableBalance ? availableBalance.toString() : "",
    currency: "USD",
    withdrawalMethod: "stripe" as "stripe" | "paypal" | "bank_account",
    notes: "",
    selectedBankAccountId: "",
  });
  
  // Get PayPal methods only
  const paypalMethods = paymentMethods.filter(method => method.type === "paypal");
  const hasPayPal = paypalMethods.length > 0;
  const paypalEmail = paypalMethods[0]?.paypalEmail;

  // Get Bank Account methods
  const bankMethods = paymentMethods.filter(method => 
    method.type === "bank_account" || 
    method.type === "us_bank_account" || 
    method.type === "sepa_bank_account"
  );
  const hasBankAccount = bankMethods.length > 0;

  useEffect(() => {
    setMounted(true);
    // Prevent body scroll when modal is open
    document.body.style.overflow = "hidden";
    
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  // Refresh payment methods when modal opens or when it becomes visible again
  useEffect(() => {
    if (mounted) {
      refreshPaymentMethods();
    }
  }, [mounted, refreshPaymentMethods]);


  // Check Stripe Connect status from user object (like StripeConnectSetup does)
  useEffect(() => {
    const checkLocalStripeStatus = async () => {
      const stripeAccountId = (user as any)?.stripeConnectedAccountId;
      
      if (!stripeAccountId) {
        setLocalStripeStatus({ hasAccount: false, isEnabled: false });
        return;
      }
      
      try {
        setCheckingStripeStatus(true);
        const status = await stripeConnectAPI.getAccountStatus(stripeAccountId);
        
        const isReady = status.chargesEnabled && status.payoutsEnabled;
        setLocalStripeStatus({
          hasAccount: true,
          isEnabled: isReady,
          accountId: stripeAccountId,
        });
      } catch (err) {
        console.error("Error checking Stripe Connect status:", err);
        setLocalStripeStatus({
          hasAccount: true,
          isEnabled: false,
          accountId: stripeAccountId,
        });
      } finally {
        setCheckingStripeStatus(false);
      }
    };

    if (mounted) {
      checkLocalStripeStatus();
    }
  }, [mounted, user]);

  // Reset states when modal closes
  const handleClose = () => {
    setSuccess(false);
    setError(null);
    setLoading(false);
    setSelectedCurrency(null);
    setFormData({
      amount: availableBalance ? availableBalance.toString() : "",
      currency: "USD",
      withdrawalMethod: "stripe",
      notes: "",
      selectedBankAccountId: "",
    });
    document.body.style.overflow = "unset";
    onClose();
  };

  // Handle currency selection
  const handleCurrencySelect = (currency: {id: string; name: string; code: string} | null) => {
    setSelectedCurrency(currency);
    setFormData({ ...formData, currency: currency?.code || "USD" });
  };

  if (!mounted) return null;

  const validateForm = () => {
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setError(t("withdrawalForm.errors.invalidAmount", { currencySymbol: getDynamicCurrencySymbol() }));
      return false;
    }

    if (parseFloat(formData.amount) < 1) {
      setError(t("withdrawalForm.errors.minimumAmount", { currencySymbol: getDynamicCurrencySymbol() }));
      return false;
    }

    if (parseFloat(formData.amount) > 10000) {
      setError(t("withdrawalForm.errors.maximumAmount", { currencySymbol: getDynamicCurrencySymbol() }));
      return false;
    }

    if (availableBalance && parseFloat(formData.amount) > availableBalance) {
      setError(t("withdrawalForm.errors.exceedsBalance", { currencySymbol: getDynamicCurrencySymbol(), balance: availableBalance.toFixed(2) }));
      return false;
    }

    if (!selectedCurrency || !formData.currency) {
      setError(t("withdrawalForm.errors.selectCurrency"));
      return false;
    }

    return true;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate form first
      if (!validateForm()) {
        return;
      }

      // Check withdrawal method requirements
      if (formData.withdrawalMethod === "stripe") {
        // Check Stripe Connect status before creating withdrawal
        if (!localStripeStatus?.hasAccount) {
          setError(t("withdrawalForm.errors.stripeAccountRequired"));
          return;
        }

        if (!localStripeStatus?.isEnabled) {
          setError(t("withdrawalForm.errors.stripeNotEnabled"));
          return;
        }
      } else if (formData.withdrawalMethod === "paypal") {
        // Check if PayPal is set up
        if (!hasPayPal || !paypalEmail) {
          setError(t("withdrawalForm.errors.paypalRequired"));
          return;
        }
      } else if (formData.withdrawalMethod === "bank_account") {
        // Check if Bank Account is set up
        if (!hasBankAccount) {
          setError(t("withdrawalForm.errors.bankAccountRequired"));
          return;
        }
        if (!formData.selectedBankAccountId) {
          setError(t("withdrawalForm.errors.selectBankAccount"));
          return;
        }
      }

      // Get selected bank account details if bank_account is selected
      const selectedBankAccount = formData.withdrawalMethod === "bank_account" 
        ? bankMethods.find(method => method.id === formData.selectedBankAccountId)
        : undefined;

      await createWithdrawal({
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        withdrawalMethod: formData.withdrawalMethod,
        paypalEmail: formData.withdrawalMethod === "paypal" ? paypalEmail : undefined,
        bankAccountId: formData.withdrawalMethod === "bank_account" ? formData.selectedBankAccountId : undefined,
        notes: formData.notes,
      });

      setSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err: any) {
      console.error("Withdrawal creation error:", err);
      
      // Handle API response errors - show actual messages from backend
      if (err?.response?.data?.message) {
        // Show the actual error message from the API
        setError(err.response.data.message);
      } else if (err?.response?.data?.error) {
        // Alternative error field
        setError(err.response.data.error);
      } else if (err?.response?.data?.details?.message) {
        // Error details message
        setError(err.response.data.details.message);
      } else if (err?.response?.data?.details?.reason) {
        // Handle specific reason codes with generic messages
        const reason = err.response.data.details.reason;
        if (reason === "MISSING_STRIPE_CONNECT_ACCOUNT") {
          setError(t("withdrawalForm.errors.stripeAccountRequired"));
        } else if (reason === "INSUFFICIENT_BALANCE") {
          setError(t("withdrawalForm.errors.insufficientBalance"));
        } else if (reason === "WITHDRAWAL_LIMIT_EXCEEDED") {
          setError(t("withdrawalForm.errors.withdrawalLimitExceeded"));
        } else if (reason === "DAILY_LIMIT_EXCEEDED") {
          setError(t("withdrawalForm.errors.dailyLimitExceeded"));
        } else if (reason === "STRIPE_ERROR") {
          setError(t("withdrawalForm.errors.stripeError"));
        } else if (reason === "ACCOUNT_NOT_VERIFIED") {
          setError(t("withdrawalForm.errors.accountNotVerified"));
        } else {
          setError(`Error: ${reason}`);
        }
      } else if (err?.message) {
        // Generic error message
        setError(err.message);
      } else if (err?.response?.status) {
        // HTTP status error
        setError(t("withdrawalForm.errors.requestFailed", { status: err.response.status }));
      } else {
        setError(t("withdrawalForm.errors.unexpectedError"));
      }
    } finally {
      setLoading(false);
    }
  };


  return createPortal(
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
      style={{ backdropFilter: "blur(4px)" }}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 rounded-t-2xl">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <span className="text-2xl font-bold text-white">{getDynamicCurrencySymbol()}</span>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{t("withdrawalForm.title")}</h2>
                <p className="text-purple-100 text-sm">{t("withdrawalForm.subtitle")}</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {success ? (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="text-center py-8"
            >
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t("withdrawalForm.successTitle")}</h3>
            </motion.div>
          ) : (
            <div className="space-y-6">
              {/* Stripe Connect Status Check */}
              {/* {statusLoading ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                    <div>
                      <h4 className="text-sm font-medium text-blue-800">Checking Account Status</h4>
                      <p className="text-sm text-blue-700 mt-1">
                        Verifying your Stripe Connect account setup...
                      </p>
                    </div>
                  </div>
                </div>
              ) : stripeConnectStatus && (
                <div className={`rounded-lg p-4 ${
                  !stripeConnectStatus.hasAccount 
                    ? "bg-red-50 border border-red-200" 
                    : !stripeConnectStatus.isEnabled 
                      ? "bg-yellow-50 border border-yellow-200"
                      : "bg-green-50 border border-green-200"
                }`}>
                  <div className="flex items-start gap-3">
                    {!stripeConnectStatus.hasAccount ? (
                      <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    ) : !stripeConnectStatus.isEnabled ? (
                      <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      {!stripeConnectStatus.hasAccount ? (
                        <div>
                          <h4 className="text-sm font-medium text-red-800">Stripe Connect Account Required</h4>
                          <p className="text-sm text-red-700 mt-1">
                            You need to set up a Stripe Connect account to receive withdrawals.
                          </p>
                          <button
                            onClick={() => window.open("mailto:support@kokobeo.com")}
                            className="mt-2 inline-flex items-center gap-1 text-sm text-red-700 hover:text-red-800 underline"
                          >
                            <ExternalLink className="h-3 w-3" />
                            Contact Support
                          </button>
                        </div>
                      ) : !stripeConnectStatus.isEnabled ? (
                        <div>
                          <h4 className="text-sm font-medium text-yellow-800">Account Setup Incomplete</h4>
                          <p className="text-sm text-yellow-700 mt-1">
                            Your Stripe Connect account is not fully enabled yet.
                          </p>
                          {stripeConnectStatus.requirements && (
                            <div className="mt-2">
                              <p className="text-xs font-medium text-yellow-800">Requirements:</p>
                              <ul className="text-xs text-yellow-700 mt-1 list-disc list-inside">
                                {stripeConnectStatus.requirements.currently_due.map((req, index) => (
                                  <li key={index}>{req}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div>
                          <h4 className="text-sm font-medium text-green-800">Withdrawal Ready</h4>
                          <p className="text-sm text-green-700 mt-1">
                            Your account is set up and ready for withdrawals!
                          </p>
                          <button
                            onClick={() => refreshStripeConnectStatus()}
                            className="mt-2 inline-flex items-center gap-1 text-sm text-green-700 hover:text-green-800 underline"
                          >
                            <ExternalLink className="h-3 w-3" />
                            Refresh Status
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )} */}

              <form onSubmit={handleSubmit} className="space-y-6">
              {/* Security Notice */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-purple-800">{t("withdrawalForm.secureWithdrawal")}</h4>
                    <p className="text-sm text-purple-700 mt-1">
                      {t("withdrawalForm.secureWithdrawalDesc")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Withdrawal Method Selection */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {t("withdrawalForm.withdrawalMethod")} <span className="text-red-500">{t("withdrawalForm.required")}</span>
                  </label>
                </div>

                {/* Show message if no methods available */}
                {!(localStripeStatus?.hasAccount && localStripeStatus?.isEnabled) && !hasPayPal && !hasBankAccount && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-3">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-yellow-800">{t("withdrawalForm.noWithdrawalMethodsAvailable")}</h4>
                        <p className="text-sm text-yellow-700 mt-1">
                          {t("withdrawalForm.noWithdrawalMethodsDesc")}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-3">
                  {/* Stripe Connect - Only show when configured */}
                  {localStripeStatus?.hasAccount && localStripeStatus?.isEnabled && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, withdrawalMethod: "stripe" })}
                      className={`relative flex items-start gap-3 p-4 border-2 rounded-xl transition-all ${
                        formData.withdrawalMethod === "stripe"
                          ? "border-purple-500 bg-purple-50"
                          : "border-gray-200 hover:border-purple-300"
                      }`}
                    >
                      <div className={`flex-shrink-0 mt-0.5 ${
                        formData.withdrawalMethod === "stripe" ? "text-purple-600" : "text-gray-400"
                      }`}>
                        <CreditCard className="h-5 w-5" />
                      </div>
                      <div className="flex-1 text-left">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">{t("withdrawalForm.stripeConnect")}</span>
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        </div>
                        <p className="text-xs text-gray-600 mt-1">{t("withdrawalForm.bankPayoutViaStripe")}</p>
                      </div>
                      {formData.withdrawalMethod === "stripe" && (
                        <div className="absolute top-2 right-2">
                          <CheckCircle className="h-5 w-5 text-purple-600" />
                        </div>
                      )}
                    </button>
                  )}

                  {/* PayPal Option - Only show when added */}
                  {hasPayPal && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, withdrawalMethod: "paypal" })}
                      className={`relative flex items-start gap-3 p-4 border-2 rounded-xl transition-all ${
                        formData.withdrawalMethod === "paypal"
                          ? "border-purple-500 bg-purple-50"
                          : "border-gray-200 hover:border-purple-300"
                      }`}
                    >
                      <div className={`flex-shrink-0 mt-0.5 ${
                        formData.withdrawalMethod === "paypal" ? "text-purple-600" : "text-gray-400"
                      }`}>
                        <span className="text-lg font-bold">{getDynamicCurrencySymbol()}</span>
                      </div>
                      <div className="flex-1 text-left">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">{t("withdrawalForm.paypal")}</span>
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        </div>
                        <p className="text-xs text-gray-600 mt-1 truncate">{paypalEmail}</p>
                      </div>
                      {formData.withdrawalMethod === "paypal" && (
                        <div className="absolute top-2 right-2">
                          <CheckCircle className="h-5 w-5 text-purple-600" />
                        </div>
                      )}
                    </button>
                  )}
                </div>

                {/* Bank Account Options */}
                {hasBankAccount && (
                  <div className="grid grid-cols-1 gap-3 mt-3">
                    <div className="space-y-2">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, withdrawalMethod: "bank_account" })}
                        className={`relative flex items-start gap-3 p-4 border-2 rounded-xl transition-all w-full ${
                          formData.withdrawalMethod === "bank_account"
                            ? "border-purple-500 bg-purple-50"
                            : "border-gray-200 hover:border-purple-300"
                        }`}
                      >
                        <div className={`flex-shrink-0 mt-0.5 ${
                          formData.withdrawalMethod === "bank_account" ? "text-purple-600" : "text-gray-400"
                        }`}>
                          <Building2 className="h-5 w-5" />
                        </div>
                        <div className="flex-1 text-left">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">{t("withdrawalForm.bankAccount")}</span>
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          </div>
                          <p className="text-xs text-gray-600 mt-1">{t("withdrawalForm.manualBankTransfer")}</p>
                        </div>
                        {formData.withdrawalMethod === "bank_account" && (
                          <div className="absolute top-2 right-2">
                            <CheckCircle className="h-5 w-5 text-purple-600" />
                          </div>
                        )}
                      </button>

                      {/* Bank Account Selection Dropdown - Show when bank_account is selected */}
                      {formData.withdrawalMethod === "bank_account" && (
                        <div className="ml-4 pl-4 border-l-2 border-purple-200">
                          <label className="block text-xs font-medium text-gray-700 mb-2">
                            {t("withdrawalForm.selectBankAccount")} <span className="text-red-500">{t("withdrawalForm.required")}</span>
                          </label>
                          <select
                            value={formData.selectedBankAccountId}
                            onChange={(e) => setFormData({ ...formData, selectedBankAccountId: e.target.value })}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          >
                            <option value="">{t("withdrawalForm.selectBankAccountPlaceholder")}</option>
                            {bankMethods.map(method => (
                              <option key={method.id} value={method.id}>
                                {method.bankName} - {method.accountHolderName} 
                                {method.type === "sepa_bank_account" 
                                  ? ` (****${method.iban?.slice(-4)})` 
                                  : ` (****${method.last4 || method.accountNumber?.slice(-4)})`
                                }
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Setup Methods Link - Show when no methods available */}
                {!(localStripeStatus?.hasAccount && localStripeStatus?.isEnabled) && !hasPayPal && !hasBankAccount && onNavigateToWithdrawalMethods && (
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => {
                        handleClose();
                        onNavigateToWithdrawalMethods();
                      }}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
                    >
                      <Plus className="h-4 w-4" />
                      {t("withdrawalForm.setupWithdrawalMethods")}
                    </button>
                  </div>
                )}
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("withdrawalForm.amount")} <span className="text-red-500">{t("withdrawalForm.required")}</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-lg font-bold text-gray-400">{getDynamicCurrencySymbol()}</span>
                  <input
                    type="number"
                    step="0.01"
                    min="1"
                    max="10000"
                    value={formData.amount}
                    onChange={(e) => {
                      setFormData({ ...formData, amount: e.target.value });
                      setError(null); // Clear any previous errors when user types
                    }}
                    className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors ${
                      formData.amount && (
                        parseFloat(formData.amount) < 1 || 
                        parseFloat(formData.amount) > 10000 ||
                        (availableBalance && parseFloat(formData.amount) > availableBalance)
                      )
                        ? "border-red-300"
                        : "border-gray-300"
                    }`}
                    placeholder={t("withdrawalForm.amountPlaceholder")}
                    required
                  />
                </div>
                {formData.amount && parseFloat(formData.amount) < 1 && (
                  <p className="text-red-500 text-xs mt-1">{t("withdrawalForm.minimumWithdrawal", { currencySymbol: getDynamicCurrencySymbol() })}</p>
                )}
                {formData.amount && parseFloat(formData.amount) > 10000 && (
                  <p className="text-red-500 text-xs mt-1">{t("withdrawalForm.maximumWithdrawal", { currencySymbol: getDynamicCurrencySymbol() })}</p>
                )}
                {formData.amount && availableBalance && parseFloat(formData.amount) > availableBalance && (
                  <p className="text-red-500 text-xs mt-1">
                    {t("withdrawalForm.exceedsAvailableBalance", { currencySymbol: getDynamicCurrencySymbol(), balance: availableBalance.toFixed(2) })}
                  </p>
                )}
                <div className="flex items-center justify-between mt-2">
                  <p className="text-gray-500 text-xs">{t("withdrawalForm.minMaxInfo", { currencySymbol: getDynamicCurrencySymbol() })}</p>
                  {availableBalance && availableBalance > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setFormData({ ...formData, amount: availableBalance.toString() });
                        setError(null);
                      }}
                      className="text-blue-600 hover:text-blue-700 text-xs font-medium underline"
                    >
                      {t("withdrawalForm.useAvailableBalance", { currencySymbol: getDynamicCurrencySymbol(), balance: availableBalance.toFixed(2) })}
                    </button>
                  )}
                </div>
                {availableBalance && (
                  <p className="text-blue-600 text-xs mt-1 font-medium">
                    {t("withdrawalForm.availableBalance", { currencySymbol: getDynamicCurrencySymbol(), balance: availableBalance.toFixed(2) })}
                  </p>
                )}
              </div>

              {/* Currency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("withdrawalForm.currency")} <span className="text-red-500">{t("withdrawalForm.required")}</span>
                </label>
                <CurrencyDropdown
                  selectedCurrency={selectedCurrency}
                  onCurrencySelect={handleCurrencySelect}
                  placeholder={t("withdrawalForm.selectCurrency")}
                />
                {!selectedCurrency && (
                  <p className="text-gray-500 text-xs mt-1">
                    {t("withdrawalForm.selectCurrencyHelper")}
                  </p>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("withdrawalForm.notes")}
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors resize-none"
                  placeholder={t("withdrawalForm.notesPlaceholder")}
                />
              </div>

              {/* Error Message */}
              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="bg-red-50 border border-red-200 rounded-lg p-4"
                  >
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="text-sm text-red-700">
                          {error}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  className="flex-1 h-12"
                  disabled={loading}
                >
                  {t("withdrawalForm.cancel")}
                </Button>
                <Button
                  type="submit"
                  disabled={
                    loading || 
                    checkingStripeStatus ||
                    !formData.amount ||
                    // Check PayPal email if PayPal is selected
                    (formData.withdrawalMethod === "paypal" && !hasPayPal) ||
                    // Check bank account selection if bank_account is selected
                    (formData.withdrawalMethod === "bank_account" && !formData.selectedBankAccountId)
                  }
                  className="flex-1 h-12 bg-purple-600 hover:bg-purple-700"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      {t("withdrawalForm.creating")}
                    </>
                  ) : (
                    <>
                      <span className="text-lg font-bold mr-2">{getDynamicCurrencySymbol()}</span>
                      {t("withdrawalForm.createWithdrawal")}
                    </>
                  )}
                </Button>
              </div>
              </form>
            </div>
          )}
        </div>
      </motion.div>
    </div>,
    document.body
  );
};

export default WithdrawalForm;
